import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useTheme } from "../../../contexts/ThemeContext";

import { vehicleGroupService } from "../../../Services/vehicle.group.service";
import { vehicleService } from "../../../Services/vehicle.service";

import { FaMapMarkedAlt, FaCar } from "react-icons/fa";
import { HiOutlineRectangleGroup } from "react-icons/hi2";

import CryptoJS from "crypto-js";
import { useTranslation } from "react-i18next";
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
} from "@syncfusion/ej2-react-grids";
import { storage } from "../../../Services/storage.service";
import { storageKeys } from "../../../Constants/storage.key";

const vehicleIdOf = (v) => Number(v?.vehicleId ?? v?.id ?? v?.groupVehicleId) || 0;
const vehicleLabelOf = (v) => v?.alias ?? v?.plateAlias ?? v?.plate ?? v?.licensePlate ?? "";

const resolveRadarIds = (row) => ({
  gId: Number(row?.groupId ?? row?.GroupId ?? row?.vehicleGroupId ?? row?.VehicleGroupId ?? 0) || 0,
  vId: Number(row?.vehicleId ?? row?.VehicleId ?? row?.id ?? row?.Id ?? 0) || 0,
  sv: row?.showVisits === true || row?.showVisitPoints === "S" || row?.ShowVisitPoints === "S",
});

const normalizeRadarItem = (item) => {
  const { gId, vId, sv } = resolveRadarIds(item);
  return {
    ...item,
    groupId: gId,
    vehicleGroupId: gId,
    vehicleId: vId,
    showVisits: sv,
  };
};

export default function ViewMapFilters() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isDark } = useTheme();

  const [viewVisits, setViewVisits] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState(0);
  const [selectedVehicleId, setSelectedVehicleId] = useState(0);

  const [filtersLoading, setFiltersLoading] = useState(false);
  const [isLoadingVehicles, setIsLoadingVehicles] = useState(false);
  const [vehicleGroups, setVehicleGroups] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [allVehicles, setAllVehicles] = useState([]);
  const [radarHistory, setRadarHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    dispatch({ type: "title", value: t("VIEW_MAP_FILTERS.TITLE") });
  }, [dispatch, t]);

  const loadVehiclesForGroup = async (groupId) => {
    setIsLoadingVehicles(true);
    try {
      const response = await vehicleService.fetchVehicles(groupId);
      const mapped = Array.isArray(response) ? response : [];
      setVehicles(mapped);
      return mapped;
    } catch {
      setVehicles([]);
      return [];
    } finally {
      setIsLoadingVehicles(false);
    }
  };

  const fetchVehicleGroups = async () => {
    try {
      setFiltersLoading(true);
      setIsLoadingVehicles(true);
      const [groups, allV] = await Promise.all([
        vehicleGroupService.fetchGroups(),
        vehicleService.fetchVehicles(0),
      ]);
      setVehicleGroups(Array.isArray(groups) ? groups : []);
      const mapped = Array.isArray(allV) ? allV : [];
      setAllVehicles(mapped);
      setVehicles(mapped);
    } catch {
      setVehicleGroups([]);
      setVehicles([]);
      setAllVehicles([]);
    } finally {
      setFiltersLoading(false);
      setIsLoadingVehicles(false);
    }
  };

  const handleGroupChange = async (e) => {
    const gId = Number(e.target.value) || 0;
    setSelectedGroupId(gId);
    setSelectedVehicleId(0);
    if (gId === 0) {
      setVehicles(allVehicles);
      return;
    }
    await loadVehiclesForGroup(gId);
  };

  const handleVehicleChange = (e) => {
    setSelectedVehicleId(Number(e.target.value) || 0);
  };

  const loadRadarHistory = async () => {
    setHistoryLoading(true);
    try {
      const data = await vehicleService.fetchRadars();
      const list = Array.isArray(data)
        ? data
        : data?.items ?? data?.radars ?? data?.result ?? (data ? [data] : []);
      setRadarHistory(list.slice(0, 10).map(normalizeRadarItem));
    } catch {
      // silencioso
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    fetchVehicleGroups();
    loadRadarHistory();
  }, []);



  const saveAndOpenMap = async (gId, vId, sv, gName = "", vName = "") => {
    const secretKey = process.env.REACT_APP_CRYPTO_SECRET;
    const groupCrypto = encodeURIComponent(CryptoJS.AES.encrypt(String(gId), secretKey).toString());
    const vehicleCrypto = encodeURIComponent(CryptoJS.AES.encrypt(String(vId), secretKey).toString());
    const visitsCrypto = encodeURIComponent(CryptoJS.AES.encrypt(String(sv), secretKey).toString());
    const url = `/#/viewmap/${groupCrypto}/${vehicleCrypto}/${visitsCrypto}`;

    window.__radarLock__ = { groupId: gId, vehicleId: vId, groupName: gName, vehicleName: vName };
    window.open(url, "_blank", "popup,toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes");

    try {
      const radarUserId = Number(storage.get(storageKeys.settings)?.userId) || 0;
      await vehicleService.saveRadar({
        radarUserId,
        groupId: gId,
        vehicleId: vId,
        showVisitPoints: sv ? "S" : "N",
        isGrouped: "N",
        isDarkMode: "N",
      });
    } catch {
      // silencioso
    } finally {
      await loadRadarHistory();
    }
  };

  const showMap = () => {
    const groupName = getGroupName(selectedGroupId);
    const vehicleName = getVehicleName(selectedVehicleId);
    saveAndOpenMap(selectedGroupId, selectedVehicleId, viewVisits, groupName, vehicleName);
  };

  const getGroupName = (groupId) => {
    const g = vehicleGroups.find((g) => g.vehicleGroupId === groupId);
    return g ? g.vehicleGroupName ?? g.description ?? g.name ?? groupId : groupId || t("VIEW_MAP_FILTERS.ALL_GROUPS");
  };

  const getVehicleName = (vehicleId) => {
    const v = allVehicles.find((v) => vehicleIdOf(v) === vehicleId);
    return v ? vehicleLabelOf(v) : vehicleId || t("VIEW_MAP_FILTERS.ALL_VEHICLES");
  };

  const reopenFromHistory = (row) => {
    const { gId, vId, sv } = resolveRadarIds(row);
    saveAndOpenMap(gId, vId, sv, row?.groupName, row?.vehicleName);
  };

  const gridData = radarHistory.map((item) => ({
    ...item,
    groupName: item.groupName ?? item.groupDescription ?? getGroupName(item.groupId ?? item.vehicleGroupId),
    vehicleName: item.vehicleName ?? item.plate ?? item.licensePlate ?? getVehicleName(item.vehicleId),
    showVisitsLabel: item.showVisits ? t("COMMON.YES") : t("COMMON.NO"),
  }));

  const actionTemplate = (props) => (
    <button
      className="btn btn-sm btn-outline-primary"
      style={isDark ? { color: "#fff", borderColor: "#fff" } : {}}
      onClick={() => reopenFromHistory(props)}
    >
      <FaMapMarkedAlt size={13} style={{ marginRight: 4, color: isDark ? "#fff" : undefined }} />
      {t("VIEW_MAP_FILTERS.OPEN_VISUALIZATION")}
    </button>
  );

  const selectStyle = {
    height: 36,
    fontSize: 11,
    padding: "0 8px",
    borderRadius: 4,
    border: isDark ? "1px solid var(--border-secondary, #444a5e)" : "1px solid #ced4da",
    backgroundColor: isDark ? "var(--bg-primary, #2b2c40)" : "#fff",
    color: isDark ? "var(--text-primary, #fff)" : "#212529",
    width: "100%",
  };

  const optionStyle = {
    backgroundColor: isDark ? "var(--bg-primary, #2b2c40)" : "#fff",
    color: isDark ? "var(--text-primary, #fff)" : "#212529",
  };

  const labelStyle = {
    fontSize: 10,
    fontWeight: 500,
    marginBottom: 4,
    display: "flex",
    alignItems: "center",
    gap: 6,
  };

  return (
    <div className="container-xxl flex-grow-1 container-p-y">
      <div className="nav-align-top">

        <div className="row" style={{ justifyContent: "flex-start", alignItems: "flex-end" }}>
          <div className="mb-1 col-md-4">
            <div style={labelStyle}>
              <HiOutlineRectangleGroup size={18} />
              {t("VIEW_MAP_FILTERS.GROUPS")}
            </div>
            <select
              style={selectStyle}
              value={selectedGroupId}
              disabled={filtersLoading}
              onChange={handleGroupChange}
            >
              <option value={0} style={optionStyle}>{t("VIEW_MAP_FILTERS.ALL_GROUPS")}</option>
              {vehicleGroups.map((g) => (
                <option key={g.vehicleGroupId} value={g.vehicleGroupId} style={optionStyle}>
                  {g.vehicleGroupName ?? g.description ?? g.name}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-1 col-md-4">
            <div style={labelStyle}>
              <FaCar size={16} />
              {t("VIEW_MAP_FILTERS.VEHICLES")}
            </div>
            <select
              style={selectStyle}
              value={selectedVehicleId}
              disabled={filtersLoading || isLoadingVehicles}
              onChange={handleVehicleChange}
            >
              <option value={0} style={optionStyle}>
                {isLoadingVehicles ? `${t("COMMON.LOADING")}...` : t("VIEW_MAP_FILTERS.ALL_VEHICLES")}
              </option>
              {vehicles.map((v) => {
                const id = vehicleIdOf(v);
                if (!id) return null;
                return (
                  <option key={id} value={id} style={optionStyle}>
                    {vehicleLabelOf(v)}
                  </option>
                );
              })}
            </select>
          </div>

          <div className="mb-1 col-md-2 offset-md-1" style={{ display: "flex", alignItems: "flex-end" }}>
            <div className="form-check form-switch m-2">
              <input
                className="form-check-input"
                type="checkbox"
                id="flexSwitchCheckDefault"
                onChange={() => setViewVisits((v) => !v)}
                checked={viewVisits}
              />
              <label className="form-check-label" htmlFor="flexSwitchCheckDefault">
                {t("VIEW_MAP_FILTERS.VIEW_VISITS")}
              </label>
            </div>
          </div>

          <div className="mb-1 col-md-3" style={{ display: "flex", alignItems: "flex-end" }}>
            <button
              type="button"
              disabled={filtersLoading}
              className="btn btn-primary"
              onClick={showMap}
            >
              <FaMapMarkedAlt style={{ paddingRight: 4 }} size={20} />
              {t("VIEW_MAP_FILTERS.OPEN_VISUALIZATION")}
            </button>
          </div>
        </div>

        <div className="row">
          <div className="form-text">{t("VIEW_MAP_FILTERS.HELP_TEXT")}</div>
        </div>

        <div className="row mt-4">
          <div className="col-12">

            {historyLoading && (
              <p className="text-muted small">{t("COMMON.LOADING")}...</p>
            )}

            {!historyLoading && (
              <div className="card grid-card" style={{ marginTop: 8 }}>
                <GridComponent
                  dataSource={gridData}
                  allowFiltering={false}
                  rowHeight={30}
                  height="auto"
                  locale="pt-BR"
                >
                  <ColumnsDirective>
                    <ColumnDirective field="groupName" headerText={t("VIEW_MAP_FILTERS.GROUPS")} width="150" />
                    <ColumnDirective field="vehicleName" headerText={t("VIEW_MAP_FILTERS.VEHICLES")} width="150" />
                    <ColumnDirective field="showVisitsLabel" headerText={t("VIEW_MAP_FILTERS.VIEW_VISITS")} width="120" />
                    <ColumnDirective headerText="" width="180" template={actionTemplate} />
                  </ColumnsDirective>
                </GridComponent>
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
